// src/components/healthrecords/IdentityMismatchModal.jsx
//
// DEF-023 follow-up. Shown when useAutoTriage's pipeline pauses at
// AWAITING_IDENTITY_CONFIRMATION — the uploaded report's own printed
// name/age disagrees with the patient's captured profile (see
// IdentityMismatchException on the backend). The user must explicitly say
// "yes, this is the right report" before it proceeds to triage; that
// decision — and an optional reason — becomes the PatientAuditLog comment
// for this upload.

import React, { useState } from 'react';
import Icon from '../Icon';

export default function IdentityMismatchModal({
  mismatch, onConfirm, onCancel,
  title = 'Does this report belong to you?',
  description = "The report you're uploading shows different details than your profile. Please confirm before we continue.",
  cancelLabel = 'No, cancel upload',
}) {
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!mismatch) return null;

  const { extractedName, extractedAge, profileName, profileAge } = mismatch;

  async function handleConfirm() {
    setSubmitting(true);
    try {
      await onConfirm(comment.trim() || null);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Confirm report identity"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(13,30,53,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
    >
      <div
        style={{
          width: 'min(440px, 92vw)', background: 'var(--bg-surface)',
          borderRadius: 14, boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
          padding: '22px 24px',
        }}
      >
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 14 }}>
          <div className="empty-icon empty-icon--badge" aria-hidden="true"><Icon name="alert" size={24} /></div>
          <div style={{ fontFamily: 'var(--font-display, serif)', fontSize: 17, color: 'var(--text-primary)' }}>
            {title}
          </div>
        </div>

        <p style={{ fontSize: 13.5, color: 'var(--text-secondary)', lineHeight: 1.6, margin: '0 0 16px' }}>
          {description}
        </p>

        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12,
          background: 'var(--bg-subtle)', borderRadius: 10, padding: '12px 14px', marginBottom: 16,
        }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', marginBottom: 4 }}>
              On the report
            </div>
            <div style={{ fontSize: 13.5, color: 'var(--text-primary)' }}>
              {extractedName || '—'}{extractedAge != null ? `, age ${extractedAge}` : ''}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', marginBottom: 4 }}>
              Your profile
            </div>
            <div style={{ fontSize: 13.5, color: 'var(--text-primary)' }}>
              {profileName || '—'}{profileAge != null ? `, age ${profileAge}` : ''}
            </div>
          </div>
        </div>

        <label style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>
          Anything you'd like to add? (optional)
        </label>
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="e.g. This is my mother's report — I'm uploading it on her behalf."
          rows={2}
          style={{
            width: '100%', boxSizing: 'border-box', border: '1.5px solid var(--border)',
            borderRadius: 8, padding: '9px 12px', fontSize: 13, fontFamily: 'inherit',
            resize: 'vertical', marginBottom: 18,
          }}
        />

        <div style={{ display: 'flex', gap: 10 }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            style={{
              flex: 1, padding: '11px 16px', borderRadius: 10,
              border: '1.5px solid var(--border)', background: 'var(--bg-surface)',
              color: 'var(--text-primary)', fontWeight: 600, fontSize: 13.5,
              cursor: submitting ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
            }}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting}
            style={{
              flex: 1, padding: '11px 16px', borderRadius: 10, border: 'none',
              background: 'var(--brand-dark)', color: 'var(--text-on-brand-dark)', fontWeight: 600, fontSize: 13.5,
              cursor: submitting ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
            }}
          >
            {submitting ? 'Confirming…' : 'Yes, this is correct'}
          </button>
        </div>
      </div>
    </div>
  );
}
